import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { Alert, Box } from '@mui/material';
import { getCustomerPortalCustomer } from '../../api/customerPortalApi';
import { DetailField } from '../../components/InfoCard';
import { LoadingSkeleton } from '../../components/LoadingSkeleton';
import { PageHeader } from '../../components/PageHeader';
import { SectionCard } from '../../components/SectionCard';
import { StatusBadge } from '../../components/StatusBadge';

export function CustomerPortalCustomerDetailPage() {
  const { t } = useTranslation(['customers', 'common']);
  const { id } = useParams();
  const [customer, setCustomer] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const emptyValue = t('common:empty_value');

  useEffect(() => {
    setLoading(true);
    setError('');
    getCustomerPortalCustomer(id)
      .then(setCustomer)
      .catch(() => setError(t('customers:portal.load_failed')))
      .finally(() => setLoading(false));
  }, [id, t]);

  if (loading) {
    return <LoadingSkeleton type="Detail" />;
  }

  if (error || !customer) {
    return (
      <SectionCard variant="Danger" sx={{ mb: 0 }}>
        <Alert severity="error" sx={{ bgcolor: 'transparent', p: 0 }}>
          {error || t('customers:portal.not_found')}
        </Alert>
      </SectionCard>
    );
  }

  const subtitle = customer.slug
    || customer.billing_email
    || undefined;

  return (
    <Box sx={{ width: '100%', maxWidth: '100%' }}>
      <PageHeader
        type="Detail"
        title={customer.name}
        subtitle={subtitle}
        backTo="/portal/customers"
        badge={customer.status ? (
          <StatusBadge
            status={customer.status}
            category="customer"
            size="Md"
          />
        ) : null}
      />

      <SectionCard variant="Default" sx={{ mb: 0 }}>
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
            gap: 2.5,
          }}
        >
          <DetailField
            label={t('common:slug')}
            value={customer.slug || emptyValue}
          />
          <DetailField
            label={t('customers:fields.billing_email')}
            value={customer.billing_email || emptyValue}
          />
          <DetailField label={t('common:status')}>
            {customer.status
              ? <StatusBadge status={customer.status} category="customer" size="Sm" />
              : emptyValue}
          </DetailField>
          <DetailField
            label={t('common:tenants')}
            value={customer.tenants_count != null ? customer.tenants_count : emptyValue}
          />
        </Box>
      </SectionCard>
    </Box>
  );
}
